/**
 * UpdateCard — payment method display + in-app SetupIntent update flow.
 *
 * in-app-checkout Phase 3 FIX B.
 *
 * Displays the saved default card (brand + last4 + expiry) fetched via
 * `useSavedCard`. The "Update"/"Add card" button triggers the real in-app
 * SetupIntent flow now that the backend `POST /api/billing/setup-intent`
 * endpoint exists:
 *
 *   1. Click "Update"/"Add card" → `useSetupIntent.mutateAsync()` →
 *      `{ client_secret }`.
 *   2. Render `<Elements stripe={stripePromise} options={{clientSecret, appearance}}>
 *      <PaymentElement/>` (setup mode — tokenises the card without charging).
 *   3. Submit → `stripe.confirmSetup({ elements, confirmParams: { return_url },
 *      redirect: 'if_required' })`.
 *   4. On success: extract `setupIntent.payment_method` → call
 *      `useUpdatePaymentMethod.mutate({ payment_method_id })` → on success
 *      the savedCard cache is invalidated and the form collapses.
 *
 * No-stripe fallback: when `stripePromise` resolves to null (dev without
 * VITE_STRIPE_PUBLISHABLE_KEY), the button is disabled with a tooltip note.
 * Mirrors `<InAppCheckout>`'s null-stripe degradation pattern.
 *
 * Design:
 *   - Membership aesthetic chrome (token-only, no raw hex).
 *   - STRIPE_APPEARANCE from `@/lib/stripe` (shared singleton).
 *   - Error handling via `BILLING_ERROR_COPY`.
 *   - Sentry surface tag `checkout-confirm` (consistent with InAppCheckout).
 *   - 3-state display within the panel:
 *       LOADING  — Skeleton row while useSavedCard is fetching.
 *       NO CARD  — "No card on file" + "Add card" CTA.
 *       CARD     — brand chip + •••• last4 + exp + "Update" CTA.
 *   - When the form is open:
 *       SETUP_LOADING — Skeleton while setup-intent is being created.
 *       SETUP_READY   — PaymentElement rendered for card entry.
 *       SETUP_ERROR   — error message + retry.
 *
 * Token discipline:
 *   - No raw hex in this file.
 *   - STRIPE_APPEARANCE hex is in `@/lib/stripe` (whitelisted, annotated).
 *
 * @param savedCard  — `SavedCard | null` from `useSavedCard`
 * @param isLoading  — loading state from `useSavedCard`
 * @param userId     — user id for `useUpdatePaymentMethod` cache invalidation
 */

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { CreditCard, ShieldCheck } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useSetupIntent } from '@/hooks/use-setup-intent';
import { useUpdatePaymentMethod } from '@/hooks/use-saved-card';
import { ApiError } from '@/lib/api';
import { stripePromise, STRIPE_APPEARANCE } from '@/lib/stripe';
import { BILLING_ERROR_COPY } from './billing-error-copy';
import type { SavedCard } from '@/hooks/use-saved-card';

// ---------------------------------------------------------------------------
// Card brand chip
// ---------------------------------------------------------------------------

function brandLabel(brand: string): string {
  switch (brand.toLowerCase()) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'Mastercard';
    case 'amex':
      return 'Amex';
    case 'discover':
      return 'Discover';
    default:
      return brand.charAt(0).toUpperCase() + brand.slice(1);
  }
}

function CardBrandChip({ brand }: { brand: string }) {
  return (
    <span
      className="rounded-[2px] border border-panel-border-hi bg-accent-dim px-1.5 py-0.5 font-mono text-[8px] font-semibold uppercase tracking-[0.4px] text-accent"
      data-testid="update-card-brand-chip"
    >
      {brandLabel(brand)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UpdateCardProps {
  /** Saved card data from `useSavedCard`. Null when no default card is set. */
  savedCard: SavedCard | null;
  /** Loading state from `useSavedCard`. */
  isLoading: boolean;
  /**
   * User id — passed to `useUpdatePaymentMethod` for cache invalidation.
   * Mirrors the hook-arg convention for useAuth-derived state.
   */
  userId?: string;
}

// ---------------------------------------------------------------------------
// Inner form (must be INSIDE <Elements> to access useStripe/useElements)
// ---------------------------------------------------------------------------

interface SetupFormProps {
  onDone: () => void;
  userId?: string;
}

function SetupForm({ onDone, userId }: SetupFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const updatePM = useUpdatePaymentMethod(userId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setFormError(null);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // return_url is required by Stripe even with redirect: 'if_required'.
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      const copy =
        error.type === 'validation_error'
          ? error.message ?? BILLING_ERROR_COPY.CONFIRM_PAYMENT_FAILED
          : error.type === 'card_error'
          ? BILLING_ERROR_COPY.PAYMENT_DECLINED
          : error.message ?? BILLING_ERROR_COPY.CONFIRM_PAYMENT_FAILED;

      setFormError(copy);
      setSubmitting(false);
      Sentry.captureException(
        new Error(error.message ?? 'stripe_setup_error'),
        {
          tags: {
            surface: 'checkout-confirm',
            stripe_error_type: error.type,
            ...(error.code ? { stripe_error_code: error.code } : {}),
          },
        }
      );
      return;
    }

    // Extract the confirmed payment method id.
    const pmId =
      typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : (setupIntent?.payment_method as { id?: string } | null | undefined)
            ?.id;

    if (!pmId) {
      setFormError(BILLING_ERROR_COPY.CONFIRM_PAYMENT_FAILED);
      setSubmitting(false);
      return;
    }

    try {
      await updatePM.mutateAsync({ payment_method_id: pmId });
      setSubmitting(false);
      onDone();
    } catch (err) {
      setFormError(BILLING_ERROR_COPY.UNEXPECTED_ERROR);
      setSubmitting(false);
      Sentry.captureException(err, {
        tags: {
          surface: 'checkout-confirm',
          ...(err instanceof ApiError ? { code: err.code } : {}),
        },
      });
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      data-testid="update-card-form"
      className="flex flex-col gap-4 mt-3"
    >
      <div
        data-testid="update-card-payment-element-wrapper"
        className="rounded-[3px] border border-panel-border bg-panel-bg p-4"
      >
        <PaymentElement
          id="setup-payment-element"
          options={{ layout: 'tabs' }}
        />
      </div>

      {formError && (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="update-card-form-error"
          className="rounded-[3px] border border-danger/15 bg-danger-dim px-4 py-2.5"
        >
          <p className="font-mono text-xs text-danger">{formError}</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={submitting || !stripe || !elements}
          data-testid="update-card-submit"
          className={clsx(
            'flex-1 py-3',
            'bg-accent border border-accent rounded-[3px]',
            'font-mono text-[0.78rem] font-medium uppercase tracking-[0.14em]',
            'text-void cursor-pointer hover:brightness-110 transition-[filter] duration-200',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
        >
          {submitting ? 'Saving…' : 'Save card'}
        </button>
        <Button
          type="button"
          variant="ghost"
          onClick={onDone}
          data-testid="update-card-cancel"
        >
          Cancel
        </Button>
      </div>

      <div className="flex items-center justify-center gap-1.5 text-text-dim">
        <ShieldCheck size={13} />
        <span className="font-mono text-[9px] uppercase tracking-[0.8px]">
          Secured by Stripe · Card data never touches our servers
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * UpdateCard — renders the saved-card row with an in-app SetupIntent update
 * flow. Designed to be used INSIDE `<Panel title="Payment method">`.
 */
export function UpdateCard({ savedCard, isLoading, userId }: UpdateCardProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<
    import('@stripe/stripe-js').Stripe | null
  >(null);

  const { mutateAsync: createSetupIntent, isPending: setupPending } =
    useSetupIntent();

  // Resolve the Stripe singleton (module-level promise → state).
  useEffect(() => {
    stripePromise.then((s) => {
      if (s) setStripe(s);
    });
  }, []);

  async function handleOpenForm() {
    setSetupError(null);
    setClientSecret(null);
    setFormOpen(true);
    try {
      const result = await createSetupIntent();
      setClientSecret(result.client_secret);
    } catch (err) {
      setSetupError(BILLING_ERROR_COPY.SUBSCRIPTION_CREATE_FAILED);
      Sentry.captureException(err, {
        tags: {
          surface: 'checkout-confirm',
          ...(err instanceof ApiError ? { code: err.code } : {}),
        },
      });
    }
  }

  function handleDone() {
    setFormOpen(false);
    setClientSecret(null);
    setSetupError(null);
  }

  // --- Loading (saved card fetching) ---
  if (isLoading) {
    return (
      <div data-testid="update-card-loading">
        <Skeleton className="h-8" />
      </div>
    );
  }

  // --- SetupIntent form open ---
  if (formOpen) {
    // While setup-intent is being created, show a skeleton.
    if (setupPending || (!clientSecret && !setupError)) {
      return (
        <div
          data-testid="update-card-setup-loading"
          className="flex flex-col gap-3"
        >
          <Skeleton className="h-8" />
          <Skeleton className="h-40" />
        </div>
      );
    }

    if (setupError) {
      return (
        <div
          data-testid="update-card-setup-error"
          className="flex flex-col gap-3"
        >
          <div className="rounded-[3px] border border-danger/15 bg-danger-dim px-4 py-2.5">
            <p
              data-testid="update-card-setup-error-message"
              className="font-mono text-xs text-danger"
            >
              {setupError}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={handleDone}
            data-testid="update-card-setup-retry-cancel"
          >
            Cancel
          </Button>
        </div>
      );
    }

    // Stripe not yet loaded — degrade gracefully.
    if (!stripe) {
      return (
        <div
          data-testid="update-card-no-stripe"
          className="flex flex-col gap-2"
        >
          <p className="font-mono text-xs text-text-muted">
            Payment system unavailable. Please refresh and try again.
          </p>
          <Button
            variant="ghost"
            onClick={handleDone}
            data-testid="update-card-cancel"
          >
            Cancel
          </Button>
        </div>
      );
    }

    const options: StripeElementsOptions = {
      clientSecret: clientSecret!,
      appearance: STRIPE_APPEARANCE,
    };

    return (
      <div data-testid="update-card-form-wrapper">
        <Elements stripe={stripe} options={options}>
          <SetupForm onDone={handleDone} userId={userId} />
        </Elements>
      </div>
    );
  }

  // --- No card ---
  if (!savedCard) {
    return (
      <div
        className="flex items-center justify-between gap-4"
        data-testid="update-card-no-card"
      >
        <div className="flex items-center gap-2.5">
          <CreditCard size={16} className="text-text-dim" aria-hidden />
          <span className="font-body text-sm text-text-muted">
            No card on file
          </span>
        </div>
        <Button
          variant="secondary"
          onClick={() => void handleOpenForm()}
          data-testid="update-card-btn"
        >
          Add card
        </Button>
      </div>
    );
  }

  // --- Card on file ---
  const expMonth = String(savedCard.exp_month).padStart(2, '0');
  const expYear = String(savedCard.exp_year);

  return (
    <div
      className="flex items-center justify-between gap-4"
      data-testid="update-card-row"
    >
      <div className="flex items-center gap-2.5">
        <CreditCard size={16} className="text-text-dim" aria-hidden />
        <CardBrandChip brand={savedCard.brand} />
        <span
          className={clsx('font-mono text-xs tabular-nums text-text-muted')}
          data-testid="update-card-last4"
        >
          •••• {savedCard.last4}
        </span>
        <span
          className="font-mono text-xs tabular-nums text-text-dim"
          data-testid="update-card-expiry"
        >
          {expMonth} / {expYear}
        </span>
      </div>
      <Button
        variant="secondary"
        onClick={() => void handleOpenForm()}
        data-testid="update-card-btn"
      >
        Update
      </Button>
    </div>
  );
}
